/**
 * `.sqlprj` 프로젝트 파일 형식 — **읽고 쓰는 유일한 곳.** (예전 확장자 `.livesqlproj` 도 그대로 읽는다)
 *
 * 왜 따로 뺐나: 프로젝트 파일은 사용자가 **주고받는 파일**이라 빌드가 갈린 두 사람 사이를 오간다.
 * 예전에는 직렬화가 App.js 안에 있었고, 읽는 쪽이 파일당 `name`·`content`·`bindVariables` 만
 * 골라 담아서 **그 밖의 필드는 열자마자 사라지고 다음 저장 때 파일에서도 지워졌다.**
 * 캔버스처럼 버전을 쌓는 구조가 아니라 사용자가 같은 파일에 덮어쓰므로 되돌릴 방법이 없다.
 *
 * 규칙은 openflow docs/SCHEMA_COMPAT.md 를 따른다:
 *  - 형식 버전은 `version` 한 필드. 없으면 최초 형식('1.0')으로 본다.
 *  - **모르는 필드를 버리지 않는다.** 최상위도, 파일 항목 안쪽도 그대로 실어 나른다.
 *  - 이 빌드보다 새 형식이면 **추측해서 고치지 않는다.** 그대로 읽고 버전도 끌어내리지 않는다.
 *    필드를 보존하므로 왕복 자체는 무손실이고, 화면은 tooNew 로 사용자에게 알린다.
 *
 * 올리는 때: 저장된 프로젝트를 예전 빌드가 **잘못 읽을** 변경일 때만(필드 제거, 의미 변경).
 * 필드를 더하기만 하는 변경은 올리지 않는다 — 이제 예전 빌드가 모르는 키를 보존하기 때문이다.
 */

export const PROJECT_SCHEMA_VERSION = '1.0';
export const PROJECT_TYPE = 'livesql-project';

/**
 * 확장자 — 저장은 `.sqlprj`(2026-10-02 변경), 열기는 예전 `.livesqlproj` 도 받는다.
 * 옛 확장자를 빼면 이미 저장해 둔 프로젝트가 파일 선택 창에 안 보이고 열어도 SQL 탭 하나로 뜬다.
 * 내용(`type: 'livesql-project'`)은 그대로라 확장자만 다르다.
 */
export const PROJECT_EXT = '.sqlprj';
export const PROJECT_EXTS = ['.sqlprj', '.livesqlproj'];
export const isProjectFileName = (name) => {
    const lower = String(name || '').toLowerCase();
    return PROJECT_EXTS.some(ext => lower.endsWith(ext));
};

/**
 * 이 PC 에서만 뜻이 있는 값 — 파일에 쓰지 않는다.
 * 세션 id 를 남의 PC 로 보내면 아무 데도 안 붙고, projectId 는 열 때 새로 만든다.
 */
// diskPath — 스탠드얼론에서 이 탭을 저장·연 디스크 경로(utils/desktopFile.js). 남의 PC 에선 뜻이 없다.
const RUNTIME_ONLY = ['isDirty', 'path', 'sessionId', 'projectId', 'diskPath'];

/** '1.0' 같은 점 구분 버전 비교. 같으면 0, a 가 낮으면 음수. */
export function cmpVersion(a, b) {
  const parse = (v) => String(v || PROJECT_SCHEMA_VERSION).split('.').map(n => parseInt(n, 10) || 0);
  const [x, y] = [parse(a), parse(b)];
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (x[i] || 0) - (y[i] || 0);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * 탭 목록 → 파일 본문(JSON 문자열).
 *
 * @param files  열린 탭들 (모르는 필드가 붙어 있을 수 있다 — 그대로 나간다)
 * @param projectName
 * @param carry  읽을 때 챙겨 둔 최상위 미지 필드 (parseProjectFile 의 `carry`)
 */
export function serializeProject(files, projectName, carry) {
  const now = new Date().toISOString();
  const payload = {
    // 이 빌드가 모르는 최상위 필드를 먼저 깔고, 아는 것으로 덮는다
    ...(carry && typeof carry === 'object' ? carry : {}),
    // 읽어 온 버전을 그대로 유지한다 — 새 형식을 예전 빌드가 끌어내리면 다음에 잘못 읽힌다
    version: (carry && carry.version) || PROJECT_SCHEMA_VERSION,
    type: PROJECT_TYPE,
    projectName: projectName || '',
    createdAt: (carry && carry.createdAt) || now,
    updatedAt: now,
    files: (files || []).map((f) => {
      const out = { ...f };
      for (const k of RUNTIME_ONLY) delete out[k];
      return {
        ...out,                                   // 모르는 파일 단위 필드 보존
        id: f.id,
        // 프로젝트에 저장할 때는 탭 이름 끝의 .sql 확장자를 제거 (가독성 목적)
        name: (f.name || 'untitled').replace(/\.sql$/i, ''),
        content: f.content || '',
        bindVariables: f.bindVariables || {},
      };
    }),
  };
  return JSON.stringify(payload, null, 2);
}

/**
 * 파일 본문 → 화면이 쓸 모양. 유효성 검사 + 정규화.
 *
 * @param text 파일 내용
 * @param t    번역 함수 (모듈 스코프라 훅을 못 쓴다)
 * @returns {{ projectName, files, docVersion, tooNew, carry }}
 *   carry — 이 빌드가 모르는 최상위 필드. 저장할 때 serializeProject 에 다시 넘긴다.
 * @throws 파싱 실패 / 프로젝트 파일이 아님
 */
export function parseProjectFile(text, t) {
  let data;
  try {
    data = JSON.parse(text);
  } catch (e) {
    throw new Error(t('common:error.jsonParse', { message: e.message }));
  }
  if (!data || data.type !== PROJECT_TYPE || !Array.isArray(data.files)) {
    throw new Error(t('common:error.notProject'));
  }

  const docVersion = data.version || PROJECT_SCHEMA_VERSION;
  const tooNew = cmpVersion(docVersion, PROJECT_SCHEMA_VERSION) > 0;

  // id 는 열 때마다 새로 만든다 — 다른 프로젝트에서 온 파일과 부딪히지 않게.
  // 나머지는 **그대로 실어 나른다**: 이 빌드가 모르는 필드도 탭에 붙어 다니다가 저장 때 되돌아간다.
  const files = data.files.map((f, idx) => ({
    ...(f && typeof f === 'object' ? f : {}),
    id: `file_${Date.now()}_${idx}`,
    name: (f && f.name) || t('common:file.numbered', { n: idx + 1 }),
    content: (f && typeof f.content === 'string') ? f.content : '',
    bindVariables: (f && f.bindVariables && typeof f.bindVariables === 'object') ? f.bindVariables : {},
    isDirty: false,
    path: null,
  }));

  // 저장할 때 되돌려 줄 최상위 미지 필드 (아는 것은 저장 시점에 새로 쓴다)
  const carry = { ...data };
  for (const k of ['type', 'projectName', 'files', 'updatedAt']) delete carry[k];

  return {
    projectName: data.projectName || t('common:ui.project'),
    files,
    docVersion,
    tooNew,
    carry,
  };
}
